import React from 'react';
import {
    Home,
    Coins,
    Building2,
    PiggyBank,
    Wheat,
    Tractor,
    Users,
    Factory,
    HelpCircle,
    UserCircle,
} from 'lucide-react';
import type { CategoryItem } from '@hofsync/design-system';
import type { CategoryCount } from '../../services/documentService';

/**
 * Category icon configuration.
 * IDs match the category IDs from INDEX.yaml.
 * Names come from backend API (loaded from INDEX.yaml taxonomy) - #358
 */
export const CATEGORY_ICONS: Record<string, React.ReactNode> = {
    finance: <Coins className="w-5 h-5" />,
    administration: <Building2 className="w-5 h-5" />,
    livestock: <PiggyBank className="w-5 h-5" />,
    fields: <Wheat className="w-5 h-5" />,
    machinery: <Tractor className="w-5 h-5" />,
    employees: <Users className="w-5 h-5" />,
    buildings: <Factory className="w-5 h-5" />,
    unsorted: <HelpCircle className="w-5 h-5" />,
    personal: <UserCircle className="w-5 h-5" />,
};

/**
 * taxonomy-v2 role → feed icon name (#1406).
 *
 * After the v3/v2 cutover (#1373) a document row carries a `(domain, role)` cell.
 * The backend feed transform already resolves the row icon from this role
 * (`icon_service.get_role_icon`, mirrored here key-for-key) and the `Document`
 * organism renders it dynamically by name (`getIconByName` → `lucide-react[name]`).
 * This map is the **frontend source of truth** for that role→icon contract: it
 * documents which lucide icon each of the ~39 shipped v2 roles maps to, and the unit
 * test pins the names to real lucide-react exports so a typo can never silently
 * degrade a whole role's rows to the `FileText` default.
 *
 * The 9 v2 **domains** reuse `CATEGORY_ICONS` (a domain id equals its v1 category id),
 * so the left-nav domain nodes are already iconed; roles surface their icon on the
 * document row (the `LensePanel` renders icons only for top-level nav items).
 */
export const ROLE_ICONS: Record<string, string> = {
    // #3266: a BWA/Kontendruck is a bookkeeping computation, not a bank artefact —
    // deliberately NOT bank-doc's 'Landmark', so the feed separates the two at a glance.
    'accounting-record': 'Calculator',
    'advisory-bulletin': 'Megaphone',
    'analysis-report': 'FlaskConical',
    'audit-report': 'ClipboardCheck',
    'bank-doc': 'Landmark',
    certification: 'BadgeCheck',
    'commodity-contract': 'FileSignature',
    'consent-doc': 'FileCheck',
    'credit-note': 'ReceiptText',
    // #3595: the GS1 'Data Quality Dashboard' — a scorecard over the farm's own master data.
    // Deliberately NOT audit-report's 'ClipboardCheck': those two cells are the boundary this
    // role was carved out of, so a shared icon would re-blur it in the feed.
    'data-quality-report': 'Gauge',
    'delivery-note': 'Truck',
    email: 'Mail',
    'employment-doc': 'Briefcase',
    'field-record': 'Sprout',
    // #3266: shares 'Truck' with delivery-note/shipping-advice — the goods-movement
    // family. Shared icons are already the norm here (ReceiptText, Wallet, FileSignature).
    'freight-document': 'Truck',
    // #3389: the buyer's goods-receipt voucher — a COUNT performed on arrival, so it takes
    // its own icon rather than joining the 'Truck' goods-movement family.
    'goods-receipt': 'PackageCheck',
    'health-doc': 'HeartPulse',
    'identity-doc': 'IdCard',
    'inbound-order': 'ShoppingCart',
    // HOF-62: the payment-reminder PAIR. Mirrors backend `icon_service.ROLE_ICONS` key-for-key, as
    // this whole map does. Two glyphs rather than one shared family icon: these are the only two
    // cells describing the SAME document type running in opposite directions, and drawing them
    // identically would hide the axis the taxonomy was split on. `BellRing` is the demand ringing AT
    // the farm; `AlarmClock` the one it is running against its own customer. The icons distinguish
    // the two — they do NOT encode direction, which is carried by the German role label the backend
    // serves from INDEX.yaml ("Mahnung (Eingang)" / "Zahlungserinnerung (Ausgang)").
    'inbound-payment-reminder': 'BellRing',
    'inspection-report': 'ClipboardList',
    'insurance-doc': 'ShieldCheck',
    invoice: 'Receipt',
    letter: 'Mail',
    'manual-datasheet': 'BookOpen',
    'non-conformance': 'AlertTriangle',
    offer: 'Tag',
    'official-notice': 'Stamp',
    'order-confirmation': 'ClipboardCheck',
    'outbound-payment-reminder': 'AlarmClock',
    'packaging-label-spec': 'Package',
    'payment-advice': 'Banknote',
    'payout-statement': 'ReceiptText',
    'payroll-record': 'Wallet',
    payslip: 'Wallet',
    permit: 'ScrollText',
    'purchase-contract': 'FileSignature',
    'quality-spec': 'ListChecks',
    'registration-doc': 'FileBadge',
    'regulatory-record': 'Gavel',
    'repair-work-order': 'Wrench',
    'service-contract': 'FileSignature',
    'shipping-advice': 'Truck',
    'social-insurance-doc': 'ShieldCheck',
    'subsidy-doc': 'HandCoins',
    'treatment-record': 'Syringe',
    unsorted: 'HelpCircle',
};

/**
 * Canonical sidebar lens order. Mirrors the taxonomy-v2 domain registry
 * (`config/taxonomy-v2/INDEX.yaml` `domains:`; a domain id equals its v1 category
 * id). The backend response key order is unstable across selections, which made the
 * sidebar reshuffle when switching lenses (#716). Iterating in this fixed
 * order makes the sidebar deterministic regardless of backend dict order.
 */
export const CATEGORY_ORDER: readonly string[] = [
    'finance',
    'administration',
    'livestock',
    'fields',
    'machinery',
    'employees',
    'buildings',
    'unsorted',
    'personal',
];

/**
 * Identity of the synthetic "Alle Dokumente" lens (#2108).
 *
 * ``buildCategories`` prepends this lens ahead of every backend-served
 * category; it addresses the unfiltered flat document list — the default
 * landing surface on /documents since the tile-grid starting page was
 * retired. Consumers resolve the lens by THIS id from the lens data,
 * never by matching the German label string.
 */
export const ALL_DOCUMENTS_LENS_ID = 'all';

/**
 * Build categories with counts from backend category_counts.
 * NOTE: Subcategory names now come from backend API (loaded from INDEX.yaml) - #287
 * Uses pre-computed counts from backend (across all documents, not just current page).
 */
export function buildCategories(
    backendCounts: Record<string, CategoryCount>,
    totalDocuments: number
): CategoryItem[] {
    const categories: CategoryItem[] = [
        {
            id: ALL_DOCUMENTS_LENS_ID,
            name: 'Alle Dokumente',
            icon: <Home className="w-5 h-5" />,
            count: totalDocuments,
        },
    ];

    // Sort backend keys by canonical order so the sidebar is stable across
    // lens selections (#716). Unknown ids (e.g. a new taxonomy category not
    // yet in CATEGORY_ORDER) fall through to the end, alphabetised.
    const orderIndex = new Map(CATEGORY_ORDER.map((id, i) => [id, i]));
    const sortedEntries = Object.entries(backendCounts).sort(([a], [b]) => {
        const ai = orderIndex.get(a);
        const bi = orderIndex.get(b);
        if (ai !== undefined && bi !== undefined) return ai - bi;
        if (ai !== undefined) return -1;
        if (bi !== undefined) return 1;
        return a.localeCompare(b, 'de');
    });

    for (const [categoryId, categoryData] of sortedEntries) {
        const count = categoryData?.count || 0;
        if (count > 0) {
            const subcategories = categoryData?.subcategories
                ? Object.entries(categoryData.subcategories)
                      .map(([subId, subData]) => ({
                          id: subId,
                          name: subData.name,  // Backend-provided German name (#287)
                          count: subData.count,
                      }))
                      .sort((a, b) => a.name.localeCompare(b.name, 'de'))
                : undefined;

            categories.push({
                id: categoryId,
                name: categoryData.name || categoryId,  // Backend taxonomy name (#358)
                icon: CATEGORY_ICONS[categoryId],
                count,
                subcategories,
            });
        }
    }

    return categories;
}
